import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { dropshipProducts, influencerStats, DropshipProduct } from "@/mocks/dropship";
import AuthModal from "./components/AuthModal";
import SellerDashboard from "./components/SellerDashboard";
import BuyerDashboard from "./components/BuyerDashboard";
import InfluencerDashboard from "./components/InfluencerDashboard";
import OrderModal from "./components/OrderModal";
import MessageModal from "./components/MessageModal";
import { PlacedOrder } from "./components/OrderModal";
import { useSEO, SITE_URL } from "@/hooks/useSEO";

type Role = "seller" | "buyer" | "influencer";
interface AuthUser { name: string; email: string; role: Role; phone?: string; }

const warehouses = [
  { id: 1,  flag: "🇺🇸", region: "USA — West",    city: "Los Angeles, CA",        delivery: "1–3 days", color: "bg-red-50 border-red-100",       accent: "text-red-500" },
  { id: 2,  flag: "🇺🇸", region: "USA — East",    city: "New York, NY",           delivery: "1–3 days", color: "bg-red-50 border-red-100",       accent: "text-red-500" },
  { id: 3,  flag: "🇺🇸", region: "USA — South",   city: "Miami, FL",              delivery: "1–3 days", color: "bg-red-50 border-red-100",       accent: "text-red-500" },
  { id: 4,  flag: "🇨🇦", region: "Canada — East", city: "Toronto, ON",            delivery: "2–4 days", color: "bg-orange-50 border-orange-100", accent: "text-orange-500" },
  { id: 5,  flag: "🇨🇦", region: "Canada — West", city: "Vancouver, BC",          delivery: "2–4 days", color: "bg-orange-50 border-orange-100", accent: "text-orange-500" },
  { id: 6,  flag: "🇬🇧", region: "UK",            city: "London, England",        delivery: "2–4 days", color: "bg-sky-50 border-sky-100",       accent: "text-sky-600" },
  { id: 7,  flag: "🇩🇪", region: "Germany",       city: "Frankfurt",              delivery: "2–4 days", color: "bg-amber-50 border-amber-100",   accent: "text-amber-600" },
  { id: 8,  flag: "🇳🇱", region: "Netherlands",   city: "Rotterdam",              delivery: "2–5 days", color: "bg-amber-50 border-amber-100",   accent: "text-amber-500" },
  { id: 9,  flag: "🇫🇷", region: "France",        city: "Paris",                  delivery: "2–4 days", color: "bg-amber-50 border-amber-100",   accent: "text-amber-500" },
  { id: 10, flag: "🇦🇪", region: "UAE",           city: "Dubai",                  delivery: "2–4 days", color: "bg-yellow-50 border-yellow-100", accent: "text-yellow-600" },
  { id: 11, flag: "🇦🇺", region: "Australia",     city: "Sydney, NSW",            delivery: "3–5 days", color: "bg-emerald-50 border-emerald-100", accent: "text-emerald-600" },
  { id: 12, flag: "🇸🇬", region: "Singapore",     city: "Singapore",              delivery: "2–4 days", color: "bg-teal-50 border-teal-100",     accent: "text-teal-600" },
  { id: 13, flag: "🇯🇵", region: "Japan",         city: "Tokyo",                  delivery: "2–4 days", color: "bg-rose-50 border-rose-100",     accent: "text-rose-500" },
  { id: 14, flag: "🌎",  region: "Latin America", city: "São Paulo, Brazil",      delivery: "3–6 days", color: "bg-green-50 border-green-100",   accent: "text-green-600" },
];

const categories = ["All", "Electronics", "Health & Beauty", "Home & Garden", "Sports & Fitness"];

const platformColors: Record<string, string> = {
  YouTube: "bg-red-100 text-red-600",
  TikTok: "bg-gray-900 text-white",
  Facebook: "bg-sky-100 text-sky-600",
  "Twitter/X": "bg-gray-100 text-gray-800",
};

export default function OverseasWarehousePage() {
  useSEO({
    title: "Factory Direct Dropshipping | 14 Overseas Warehouses | Crown Global",
    description: "Factory direct dropshipping with 14 overseas warehouses across USA, Canada, Europe, Asia, and Middle East. Order now with UPS delivery in 1–6 days. Sellers, buyers, and influencers welcome.",
    keywords: "factory dropshipping, overseas warehouse, UPS delivery, factory direct, dropship products, influencer commission",
    canonical: "/overseas-warehouse",
    ogTitle: "Factory Direct Dropshipping — Crown Global",
    ogDescription: "14 overseas warehouses. Factory direct products. UPS delivery 1–6 days. Sellers, buyers, and influencers.",
    ogImage: "https://public.readdy.ai/ai/img_res/33e816537389880a84c3861c2d174cbe.jpg",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${SITE_URL}/overseas-warehouse`,
          "url": `${SITE_URL}/overseas-warehouse`,
          "name": "Factory Direct Dropshipping — Crown Global",
          "description": "Factory direct dropshipping service with 14 overseas warehouses across USA, Canada, Europe, Asia, and Middle East.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
              { "@type": "ListItem", "position": 2, "name": "Overseas Warehouse", "item": `${SITE_URL}/overseas-warehouse` }
            ]
          }
        },
        {
          "@type": "Service",
          "@id": `${SITE_URL}/overseas-warehouse#service`,
          "url": `${SITE_URL}/overseas-warehouse`,
          "name": "Crown Global Factory Direct Dropshipping",
          "description": "Factory direct dropshipping service with 14 overseas warehouses across USA, Canada, Europe, Asia, and Middle East. Fast UPS delivery in 1–6 days.",
          "provider": { "@id": `${SITE_URL}/#organization` },
          "areaServed": ["USA", "Canada", "United Kingdom", "Germany", "Netherlands", "France", "UAE", "Australia", "Singapore", "Japan", "Brazil"],
          "serviceType": "Dropshipping",
          "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock",
            "description": "Factory direct products with UPS delivery in 1–6 days from 14 overseas warehouses"
          }
        }
      ]
    }
  });

  const [showAuth, setShowAuth] = useState(false);
  const [authRole, setAuthRole] = useState<Role>("buyer");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [orderProduct, setOrderProduct] = useState<DropshipProduct | null>(null);
  const [messagingProduct, setMessagingProduct] = useState<DropshipProduct | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [sortBy, setSortBy] = useState<"popular" | "price_asc" | "price_desc">("popular");
  const [guestOrders, setGuestOrders] = useState<PlacedOrder[]>([]);

  const handleAuth = (u: AuthUser) => { setUser(u); setShowAuth(false); };
  const handleLogout = () => setUser(null);
  const openAuth = (role: Role) => { setAuthRole(role); setShowAuth(true); };

  const filteredProducts = dropshipProducts
    .filter(p => {
      const matchSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.category.toLowerCase().includes(searchQuery.toLowerCase());
      const matchCat = activeCategory === "All" || p.category === activeCategory;
      return matchSearch && matchCat;
    })
    .sort((a, b) => {
      if (sortBy === "price_asc") return a.price - b.price;
      if (sortBy === "price_desc") return b.price - a.price;
      return b.sold - a.sold;
    });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {showAuth && (
        <AuthModal onClose={() => setShowAuth(false)} onAuth={handleAuth} defaultRole={authRole} />
      )}

      {orderProduct && (
        <OrderModal
          product={orderProduct}
          onClose={() => setOrderProduct(null)}
          onOrderPlaced={(order) => {
            setGuestOrders(prev => [order, ...prev]);
            setOrderProduct(null);
          }}
          isLoggedIn={!!user}
          onLoginRequired={() => { setOrderProduct(null); openAuth("buyer"); }}
        />
      )}

      {messagingProduct && (
        <MessageModal
          productName={messagingProduct.name}
          sellerName={messagingProduct.seller}
          sellerFlag={messagingProduct.sellerFlag}
          buyerName={user?.name || "Guest"}
          onClose={() => setMessagingProduct(null)}
        />
      )}

      <div className="pt-28 pb-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">

          {/* Hero */}
          <div className="relative rounded-2xl overflow-hidden mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20bright%20overseas%20warehouse%20interior%20logistics%20center%20shelves%20packages%20workers%20professional%20photography%20wide%20angle%20vivid%20daylight%20industrial%20scale%20global%20shipping%20sunny&width=1400&height=360&seq=wh1hero&orientation=landscape"
              alt="Overseas Warehouse Dropshipping"
              className="w-full h-56 md:h-72 object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-transparent"></div>
            <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-16">
              <div className="inline-flex items-center gap-2 bg-amber-400/20 border border-amber-400/40 text-amber-300 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 w-fit">
                <i className="ri-global-line"></i> 14 Warehouses · USA · Canada · Europe · Asia · Middle East
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-2">Factory Direct Dropshipping</h1>
              <p className="text-white/80 text-sm max-w-lg mb-5">
                Factories post products. Local buyers order instantly. UPS delivers in 1–6 days. Real-time tracking included.
              </p>
              {!user && (
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => openAuth("seller")} className="bg-amber-400 hover:bg-amber-500 text-white font-bold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm">
                    <i className="ri-store-2-line mr-1.5"></i>Factory Sign Up / Login
                  </button>
                  <button onClick={() => openAuth("buyer")} className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm">
                    <i className="ri-user-line mr-1.5"></i>Buyer Sign Up / Login
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Dashboard — shown when logged in */}
          {user && (
            <div className="mb-12">
              {user.role === "seller" && <SellerDashboard user={user} onLogout={handleLogout} />}
              {user.role === "buyer" && <BuyerDashboard user={user} onLogout={handleLogout} />}
              {user.role === "influencer" && <InfluencerDashboard user={user} onLogout={handleLogout} />}
            </div>
          )}

          {/* PUBLIC PRODUCT STOREFRONT — always visible */}
          {!user && (
            <div className="mb-12">
              {/* Section header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <p className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-1">Shop Now</p>
                  <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                    {dropshipProducts.length} Factory Products Available
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">Order now · UPS shipping calculated at checkout · Track your delivery</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <span className="w-2 h-2 bg-green-400 rounded-full inline-block"></span>
                    Ships Today
                  </div>
                  <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <i className="ri-truck-line"></i> UPS Tracked
                  </div>
                </div>
              </div>

              {/* Search + sort */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search products, categories..."
                    className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors bg-white"
                  />
                </div>
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as typeof sortBy)}
                  className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white"
                >
                  <option value="popular">Most Popular</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                </select>
              </div>

              {/* Category filter */}
              <div className="flex gap-2 flex-wrap mb-6">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`text-xs font-semibold px-4 py-2 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                      activeCategory === cat
                        ? "bg-amber-400 text-white border-amber-400"
                        : "bg-white text-gray-500 border-gray-200 hover:border-amber-300"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <p className="text-xs text-gray-400 mb-4">{filteredProducts.length} products found</p>

              {/* Product grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-product-shop>
                {filteredProducts.map(p => (
                  <div key={p.id} className="bg-white border border-gray-100 rounded-xl overflow-hidden group hover:-translate-y-1 transition-transform duration-300">
                    <div className="w-full h-44 overflow-hidden relative cursor-pointer" onClick={() => setOrderProduct(p)}>
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-3">
                      <span className="text-xs bg-gray-100 text-gray-500 font-semibold px-2 py-0.5 rounded-full">{p.category}</span>
                      <h3 className="font-semibold text-gray-800 text-sm mt-2 mb-1 line-clamp-2 cursor-pointer hover:text-amber-500 transition-colors" onClick={() => setOrderProduct(p)}>
                        {p.name}
                      </h3>
                      <p className="text-xs text-gray-400 mb-2 truncate">{p.sellerFlag} {p.seller}</p>

                      {/* Warehouse badge */}
                      <div className="flex items-center gap-1 mb-2">
                        <i className="ri-building-2-line text-amber-400 text-xs"></i>
                        <span className="text-xs text-gray-500">Ships from {p.sellerCountry}</span>
                      </div>

                      <div className="flex items-center justify-between mb-3">
                        <span className="text-amber-500 font-extrabold text-lg">${p.price}</span>
                        <span className="text-xs text-gray-400">{p.sold.toLocaleString()} sold</span>
                      </div>

                      <div className="flex gap-1.5">
                        <button
                          onClick={() => setOrderProduct(p)}
                          className="flex-1 bg-amber-400 hover:bg-amber-500 text-white text-xs font-bold py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-shopping-cart-line mr-1"></i>Order Now
                        </button>
                        <button
                          onClick={() => setMessagingProduct(p)}
                          className="w-8 h-8 flex items-center justify-center border border-gray-200 hover:border-amber-300 text-gray-500 hover:text-amber-500 rounded-full transition-colors cursor-pointer shrink-0"
                          title="Message seller"
                        >
                          <i className="ri-message-3-line text-xs"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warehouse locations */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <p className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-0.5">Global Network</p>
                <h2 className="text-xl font-extrabold text-gray-900">{warehouses.length} Overseas Warehouses Worldwide</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {["USA", "Canada", "Europe", "Middle East", "Asia Pacific", "Latin America"].map(r => (
                  <span key={r} className="text-xs bg-amber-50 text-amber-600 font-semibold px-3 py-1 rounded-full border border-amber-200 whitespace-nowrap">{r}</span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
              {warehouses.map((w) => (
                <div key={w.id} className={`rounded-xl p-4 border ${w.color} hover:scale-105 transition-transform duration-200`}>
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-xl">{w.flag}</span>
                    <div className="min-w-0">
                      <h3 className="font-extrabold text-gray-900 text-xs leading-tight truncate">{w.region}</h3>
                      <p className="text-[10px] text-gray-400 truncate">{w.city}</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="bg-white/80 rounded-lg px-2 py-1 flex items-center justify-between">
                      <span className="text-[10px] text-gray-400">Delivery</span>
                      <span className={`text-[10px] font-bold ${w.accent}`}>{w.delivery}</span>
                    </div>
                    <div className="bg-white/80 rounded-lg px-2 py-1 flex items-center justify-between">
                      <span className="text-[10px] text-gray-400">Status</span>
                      <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block"></span><span className="text-[10px] font-semibold text-green-600">Active</span></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* How it works */}
          <div className="mb-12">
            <div className="text-center mb-8">
              <p className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-2">Simple Process</p>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">How It Works</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
              {[
                { icon: "ri-store-2-line", title: "Factory Signs Up", desc: "Factories register with email or phone, upload product photos, set prices." },
                { icon: "ri-image-add-line", title: "Post Products", desc: "List products with photos, descriptions, pricing and warehouse location." },
                { icon: "ri-shopping-cart-line", title: "Buyer Orders", desc: "Local buyers browse, place orders instantly with UPS shipping calculated." },
                { icon: "ri-truck-line", title: "Fast UPS Delivery", desc: "Ships from nearest warehouse. Tracking number assigned automatically." },
                { icon: "ri-checkbox-circle-line", title: "Buyer Confirms", desc: "Buyer confirms receipt. Seller gets paid. Message each other anytime." },
              ].map((s, i) => (
                <div key={s.title} className="bg-white rounded-xl p-5 relative">
                  <div className="absolute top-4 right-4 text-4xl font-extrabold text-gray-50 select-none">{String(i + 1).padStart(2, "0")}</div>
                  <div className="w-11 h-11 flex items-center justify-center bg-amber-50 rounded-xl mb-3">
                    <i className={`${s.icon} text-amber-500 text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1.5">{s.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Auth CTA cards — shown when not logged in */}
          {!user && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white rounded-2xl p-7 border-2 border-amber-100 hover:border-amber-300 transition-colors">
                <div className="w-14 h-14 flex items-center justify-center bg-amber-50 rounded-2xl mb-4">
                  <i className="ri-store-2-line text-amber-500 text-3xl"></i>
                </div>
                <h2 className="text-lg font-extrabold text-gray-900 mb-2">Factory / Seller</h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">Sign up with email or phone. Upload products, set prices, manage orders and track shipments from your dashboard.</p>
                <ul className="flex flex-col gap-2 mb-5">
                  {["Sign up with email or phone number", "Upload product photos & descriptions", "Set pricing & commission rates", "Manage orders & add tracking numbers", "Message buyers directly"].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-checkbox-circle-fill text-amber-400 shrink-0"></i>{f}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  <button onClick={() => openAuth("seller")} className="flex-1 bg-amber-400 hover:bg-amber-500 text-white font-bold py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm">Sign Up</button>
                  <button onClick={() => openAuth("seller")} className="flex-1 border border-amber-400 text-amber-500 font-bold py-2.5 rounded-full hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap text-sm">Sign In</button>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-7 border-2 border-stone-100 hover:border-stone-300 transition-colors">
                <div className="w-14 h-14 flex items-center justify-center bg-stone-50 rounded-2xl mb-4">
                  <i className="ri-shopping-cart-line text-stone-600 text-3xl"></i>
                </div>
                <h2 className="text-lg font-extrabold text-gray-900 mb-2">Local Buyer</h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">Browse factory products, place orders instantly, get UPS tracking, confirm receipt and message sellers.</p>
                <ul className="flex flex-col gap-2 mb-5">
                  {["Browse 26+ factory products", "Order with UPS shipping calculated", "Real-time tracking number", "Confirm receipt to release payment", "Message seller anytime"].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-checkbox-circle-fill text-stone-400 shrink-0"></i>{f}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  <button onClick={() => openAuth("buyer")} className="flex-1 bg-stone-800 hover:bg-stone-900 text-white font-bold py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm">Sign Up</button>
                  <button onClick={() => openAuth("buyer")} className="flex-1 border border-stone-300 text-stone-600 font-bold py-2.5 rounded-full hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap text-sm">Sign In</button>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-7 border-2 border-rose-100 hover:border-rose-300 transition-colors">
                <div className="w-14 h-14 flex items-center justify-center bg-rose-50 rounded-2xl mb-4">
                  <i className="ri-star-line text-rose-500 text-3xl"></i>
                </div>
                <h2 className="text-lg font-extrabold text-gray-900 mb-2">Influencer / Social Star</h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">Facebook, TikTok, Twitter &amp; YouTube stars — promote products and earn commission on every sale.</p>
                <ul className="flex flex-col gap-2 mb-5">
                  {["Get unique referral links per product", "Earn 10–25% commission per sale", "Real-time sales & earnings dashboard", "Withdraw via PayPal, WeChat or bank"].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-checkbox-circle-fill text-rose-400 shrink-0"></i>{f}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  <button onClick={() => openAuth("influencer")} className="flex-1 bg-rose-400 hover:bg-rose-500 text-white font-bold py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm">Sign Up</button>
                  <button onClick={() => openAuth("influencer")} className="flex-1 border border-rose-300 text-rose-500 font-bold py-2.5 rounded-full hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap text-sm">Sign In</button>
                </div>
              </div>
            </div>
          )}

          {/* Influencer Leaderboard */}
          <div className="mb-12">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
              <div>
                <p className="text-rose-500 text-sm font-semibold uppercase tracking-widest mb-1">Social Stars</p>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">Top Influencer Earners</h2>
              </div>
              <button onClick={() => openAuth("influencer")} className="hidden md:flex items-center gap-1.5 bg-rose-400 hover:bg-rose-500 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-star-line"></i> Join as Influencer
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {influencerStats.map((star, idx) => (
                <div key={star.id} className="bg-white rounded-2xl p-5 border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full font-extrabold text-sm shrink-0 ${
                      idx === 0 ? "bg-amber-400 text-white" : idx === 1 ? "bg-gray-300 text-gray-700" : idx === 2 ? "bg-orange-300 text-white" : "bg-gray-100 text-gray-500"
                    }`}>
                      {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${idx + 1}`}
                    </div>
                    <div className="w-12 h-12 rounded-full overflow-hidden shrink-0">
                      <img src={star.image} alt={star.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-bold text-gray-900 text-sm">{star.name}</span>
                        {star.verified && <i className="ri-shield-check-fill text-green-500 text-xs"></i>}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${platformColors[star.platform] || "bg-gray-100 text-gray-600"}`}>
                          <i className={`${star.platform_icon} mr-1`}></i>{star.platform}
                        </span>
                        <span className="text-xs text-gray-400">{star.flag} {star.followers}</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                      <div className="text-sm font-extrabold text-gray-900">{star.productsSold.toLocaleString()}</div>
                      <div className="text-xs text-gray-400">Sold</div>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-3 text-center">
                      <div className="text-sm font-extrabold text-amber-500">${(star.totalRevenue / 1000).toFixed(1)}K</div>
                      <div className="text-xs text-gray-400">Revenue</div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-3 text-center">
                      <div className="text-sm font-extrabold text-green-600">${star.commissionEarned.toLocaleString()}</div>
                      <div className="text-xs text-gray-400">Earned</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-amber-400 rounded-2xl p-10 text-center text-white">
            <h2 className="text-2xl md:text-3xl font-extrabold mb-3">Start Selling or Buying Today</h2>
            <p className="text-amber-100 text-sm mb-6 max-w-md mx-auto">
              Factories sign up with email or phone. Buyers order instantly with UPS tracking. No minimum inventory required.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button onClick={() => openAuth("seller")} className="bg-white text-amber-500 font-bold px-7 py-3 rounded-full hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-store-2-line mr-1.5"></i>Factory Sign Up
              </button>
              <button onClick={() => openAuth("buyer")} className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-7 py-3 rounded-full border border-white/30 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-shopping-cart-line mr-1.5"></i>Shop as Buyer
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
